import { z } from 'zod'
import { imageAssetRefSchema } from '../helpers/asset-ref.js'
import { focalPointSchema } from '../helpers/focal-point.js'

/**
 * The most photographs one room may hold.
 *
 * A cap rather than nothing, because uploads are not otherwise metered: a bug in a
 * client, or somebody with a folder of images and a loop, would otherwise fill the
 * bucket a room at a time. Twelve is more angles than a room has and still small
 * enough that a listing can sign them all without thinking about it.
 */
export const MAX_SPACE_PHOTOS = 12

/** The longest a caption may be. It is a description, not a paragraph. */
export const MAX_SPACE_PHOTO_CAPTION = 200

/**
 * Add a photograph to a room.
 *
 * `image` is the same three forms every other image on the wire takes — a storage
 * key after upload, an external URL, or an inline data URL when object storage is
 * off.
 *
 * The caption is optional but asked for, because it is the photograph's alt text
 * and these photographs reach clients: one with no description is one a screen
 * reader cannot convey.
 */
export const addSpacePhotoInputSchema = z
  .object({
    image: imageAssetRefSchema,
    caption: z.string().trim().max(MAX_SPACE_PHOTO_CAPTION).optional(),
    /** Optional at upload; usually set afterwards, by looking at the photograph. */
    focalPoint: focalPointSchema.nullable().optional(),
  })
  .strict()

export type AddSpacePhotoInput = z.infer<typeof addSpacePhotoInputSchema>

/**
 * Change a photograph's description or its focal point.
 *
 * Both optional, so a caller may send either without stating the other — moving a
 * focal point should not require re-sending a caption that has not changed.
 * The image itself is replaced by removing the photograph and adding another.
 */
export const updateSpacePhotoInputSchema = z
  .object({
    caption: z.string().trim().max(MAX_SPACE_PHOTO_CAPTION).nullable().optional(),
    /** Null clears it, so the photograph falls back to the default crop. */
    focalPoint: focalPointSchema.nullable().optional(),
  })
  .strict()

export type UpdateSpacePhotoInput = z.infer<typeof updateSpacePhotoInputSchema>

/**
 * Put a room's photographs in an order.
 *
 * The whole order, every time, rather than "move this one up": sending the full
 * list is idempotent, survives two people reordering at once without producing an
 * order neither chose, and makes "which is first" — the one shown wherever a
 * single image is needed — a thing the caller states rather than infers.
 */
export const reorderSpacePhotosInputSchema = z
  .object({
    photoIds: z
      .array(z.string().uuid())
      .min(1, 'Send the photographs in the order they should appear.')
      .max(MAX_SPACE_PHOTOS)
      .refine((ids) => new Set(ids).size === ids.length, 'Each photograph can only appear once.'),
  })
  .strict()

export type ReorderSpacePhotosInput = z.infer<typeof reorderSpacePhotosInputSchema>
